/**
 * Страница телефона /mobile-location?token=... (PROJECT_NOTES §37): открывается из QR-кода профиля без входа.
 * Для кого ставится точка, знает только ссылка; после сохранения ссылка гаснет, а профиль на компьютере
 * получает точку сам. Старая страница /mobile-geo оставлена для QR-кода прежнего кабинета аптеки.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircleOutline, LinkOffOutlined, PinDropOutlined } from '@mui/icons-material';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { fetchHandoffInfo, handoffGeoBackend, HandoffInfo, SavedPoint } from './geoApi';
import { formatCountdown } from './geoMath';
import GeoWizard from './GeoWizard';
import { BORDER, cardSx, hintSx, iconBoxSx, MUTED, TEXT } from './geoStyles';

type PageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; info: HandoffInfo }
  | { kind: 'done'; info: HandoffInfo; geo: SavedPoint };

const NO_TOKEN = 'В ссылке нет кода. Откройте профиль на компьютере и отсканируйте QR-код еще раз';
const EXPIRED = 'Срок ссылки истек. Откройте профиль на компьютере и покажите новый QR-код';

const MobileLocationPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const backend = useMemo(() => handoffGeoBackend(token), [token]);
  const [state, setState] = useState<PageState>(() => (token ? { kind: 'loading' } : { kind: 'error', message: NO_TOKEN }));
  const [now, setNow] = useState(() => Date.now());

  // В public/index.html нет meta viewport (кабинеты сделаны под компьютер), а без нее телефон верстает страницу
  // шириной 980px и мастер выходит мелким. Ставим ее только на время этой страницы
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchHandoffInfo(token)
      .then(info => { if (!cancelled) setState({ kind: 'ready', info }); })
      .catch(err => { if (!cancelled) setState({ kind: 'error', message: apiErrorMessage(err, 'Не удалось открыть ссылку') }); });
    return () => { cancelled = true; };
  }, [token]);

  const ready = state.kind === 'ready';
  useEffect(() => {
    if (!ready) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [ready]);

  const msLeft = state.kind === 'ready' ? state.info.deadline - now : 0;
  // Срок вышел, пока точка не сохранена: сервер все равно откажет, поэтому мастер не показываем
  const expired = state.kind === 'ready' && msLeft <= 0;
  const info = state.kind === 'ready' || state.kind === 'done' ? state.info : null;

  const handleSaved = (geo: SavedPoint) => {
    if (info) setState({ kind: 'done', info, geo });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* <Box sx={{ ...iconBoxSx, width: 36, height: 36, borderRadius: '10px' }}><PinDropOutlined sx={{ fontSize: 20 }} /></Box> */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="h1" sx={{ fontWeight: 700, color: TEXT, fontSize: '1rem', lineHeight: 1.3 }}>Местоположение аптеки</Typography>
          {info && (
            <Typography sx={{ color: MUTED, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {info.fullName ? `${info.fullName} (${info.username})` : info.username}
            </Typography>
          )}
        </Box>
        {state.kind === 'ready' && !expired && (
          <Typography sx={{ color: MUTED, fontSize: 13, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatCountdown(msLeft)}</Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, py: 2 }}>
        {state.kind === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>
        )}

        {(state.kind === 'error' || expired) && (
          <Box sx={{ ...cardSx, p: 3, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
            <LinkOffOutlined sx={{ fontSize: 44, color: '#94a3b8' }} />
            <Typography sx={{ fontWeight: 700, color: TEXT }}>Ссылка не работает</Typography>
            <Typography sx={hintSx}>{state.kind === 'error' ? state.message : EXPIRED}</Typography>
          </Box>
        )}

        {state.kind === 'ready' && !expired && (
          <GeoWizard compact backend={backend} initial={state.info.saved} onSaved={handleSaved} />
        )}

        {state.kind === 'done' && (
          <Box sx={{ ...cardSx, p: 3, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
            <CheckCircleOutline sx={{ fontSize: 52, color: '#16a34a' }} />
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '1.05rem' }}>Точка сохранена</Typography>
            <Typography sx={{ color: TEXT, fontSize: '0.9rem', lineHeight: 1.5 }}>
              {state.geo.address || 'Адрес по точке определить не удалось, но координаты сохранены'}
            </Typography>
            <Typography sx={hintSx}>Профиль на компьютере обновится сам. Эту страницу можно закрыть.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MobileLocationPage;
